// Constants
export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_CATEGORY = 'UTILITY';
export const COMPONENT_TYPES = {
  HEADER: 'HEADER',
  BODY: 'BODY',
  BUTTONS: 'BUTTONS',
};
export const MEDIA_FORMATS = ['IMAGE', 'VIDEO', 'DOCUMENT'];

export const findComponentByType = (template, type) =>
  template.components?.find(component => component.type === type);

export const processVariable = str => {
  return str.replace(/{{|}}/g, '');
};

export const allKeysRequired = value => {
  const keys = Object.keys(value);
  return keys.every(key => value[key]);
};

// Canonical list of NAMED template variables that the backend resolves
// automatically when sent blank. MUST mirror
// Whatsapp::NamedTemplateVarsResolver::STATIC_VAR_MAP keys (Ruby). If you add a
// var there, add it here too.
export const AUTO_RESOLVABLE_NAMED_VARS = [
  'contact_name',
  'contact_first_name',
  'contact_last_name',
  'contact_email',
  'contact_phone',
  'contact_id',
  'conversation_id',
  'inbox_id',
  'inbox_name',
  'agent_name',
  'agent_first_name',
  'agent_last_name',
  'agent_email',
];

// Backend also resolves custom attributes via these prefixes
// (contact_custom_<key> / conversation_custom_<key>).
const AUTO_RESOLVABLE_PREFIXES = ['contact_custom_', 'conversation_custom_'];

// True when a NAMED variable is auto-filled server-side, so the agent may
// leave it blank in the send form.
export const isAutoResolvableNamedVar = key =>
  AUTO_RESOLVABLE_NAMED_VARS.includes(key) ||
  AUTO_RESOLVABLE_PREFIXES.some(prefix => String(key).startsWith(prefix));

// Validates that every REQUIRED body param has a value. For NAMED templates,
// auto-resolvable variables may be left blank (the backend fills them via
// Whatsapp::NamedTemplateVarsResolver). For positional templates every value
// is required.
export const requiredBodyKeysFilled = (body, isNamed = false) => {
  if (!body) return true;
  return Object.keys(body).every(key => {
    if (isNamed && isAutoResolvableNamedVar(key)) return true;
    return Boolean(body[key]);
  });
};

export const replaceTemplateVariables = (templateText, processedParams) => {
  return templateText.replace(/{{([^}]+)}}/g, (match, variable) => {
    const variableKey = processVariable(variable);
    return processedParams.body?.[variableKey] || `{{${variable}}}`;
  });
};

export const buildTemplateParameters = (template, hasMediaHeaderValue) => {
  const allVariables = {};

  const bodyComponent = findComponentByType(template, COMPONENT_TYPES.BODY);
  const headerComponent = findComponentByType(template, COMPONENT_TYPES.HEADER);

  if (!bodyComponent) return allVariables;

  const templateString = bodyComponent.text;

  // Process body variables
  const matchedVariables = templateString.match(/{{([^}]+)}}/g);
  if (matchedVariables) {
    allVariables.body = {};
    matchedVariables.forEach(variable => {
      const key = processVariable(variable);
      allVariables.body[key] = '';
    });
  }

  if (hasMediaHeaderValue) {
    if (!allVariables.header) allVariables.header = {};
    allVariables.header.media_url = '';
    allVariables.header.media_type = headerComponent.format.toLowerCase();

    // For document templates, include media_name field for filename support
    if (headerComponent.format.toLowerCase() === 'document') {
      allVariables.header.media_name = '';
    }
  }

  // Process button variables
  const buttonComponents = template.components.filter(
    component => component.type === COMPONENT_TYPES.BUTTONS
  );

  buttonComponents.forEach(buttonComponent => {
    if (buttonComponent.buttons) {
      buttonComponent.buttons.forEach((button, index) => {
        // Handle URL buttons with variables
        if (button.type === 'URL' && button.url && button.url.includes('{{')) {
          const buttonVars = button.url.match(/{{([^}]+)}}/g) || [];
          if (buttonVars.length > 0) {
            if (!allVariables.buttons) allVariables.buttons = [];
            allVariables.buttons[index] = {
              type: 'url',
              parameter: '',
              url: button.url,
              variables: buttonVars.map(v => processVariable(v)),
            };
          }
        }

        // Handle copy code buttons
        if (button.type === 'COPY_CODE') {
          if (!allVariables.buttons) allVariables.buttons = [];
          allVariables.buttons[index] = {
            type: 'copy_code',
            parameter: '',
          };
        }
      });
    }
  });

  return allVariables;
};
